/**
 * The client half of the prerender contract: hydrate a document that was
 * prerendered for THIS path, mount from scratch everywhere else.
 *
 * Branch on whether this path's markup is present, not on the build mode.
 * One build serves both cases — `vite dev` and every route a manifest does
 * not name get the untouched shell, and a document can be served somewhere
 * it was not rendered for (apps/retells' SPA fallback, its service worker's
 * offline fallback). `hydrateRoot` against a shell logs a mismatch on every
 * load; `createRoot` against a prerendered document throws the markup away,
 * which is the whole point of having rendered it.
 *
 * The signal is `data-prerendered-path` on the mount point, written by
 * ./pass.ts. Asking whether `#root` has children would not do: the shell's
 * `<!--app-html-->` placeholder is a child node, so `vite dev` would take the
 * hydrate branch against nothing. Naming the PATH rather than setting a flag
 * is what catches the served-elsewhere case.
 */
import type { ReactNode } from "react";
import { createRoot, hydrateRoot, type Root } from "react-dom/client";
import { PRERENDERED_PATH_ATTRIBUTE } from "./route";

/** Was `container` prerendered for `pathname`? */
export function isPrerenderedFor(
  container: HTMLElement,
  pathname: string,
): boolean {
  return container.getAttribute(PRERENDERED_PATH_ATTRIBUTE) === pathname;
}

/**
 * Hydrate `tree` into `container` if the document was prerendered for
 * `pathname` (default: the current location), otherwise clear the container
 * and mount from scratch.
 *
 * Clearing is deliberate: a document served somewhere it was not rendered for
 * lands in the second branch, and React would otherwise repair one page's
 * markup into another's, painting the wrong page first.
 */
export function mountOrHydrate(
  container: HTMLElement,
  tree: ReactNode,
  pathname: string = location.pathname,
): Root {
  if (isPrerenderedFor(container, pathname)) {
    return hydrateRoot(container, tree);
  }
  container.replaceChildren();
  const root = createRoot(container);
  root.render(tree);
  return root;
}
